
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/feature/Header';
import BottomNavigation from '../../components/feature/BottomNavigation';
import ProductCard from '../../components/feature/ProductCard';
import { featuredProducts, categories, bannerSlides } from '../../mocks/products';

export default function HomePage() {
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [cartItems, setCartItems] = useState<any[]>([]);
  const [showAddedMessage, setShowAddedMessage] = useState(false);
  const [addedProduct, setAddedProduct] = useState<any>(null);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  const handleAddToCart = (product: any) => {
    // البحث عن المنتج في السلة
    const existingItem = cartItems.find(item => item.id === product.id);
    
    if (existingItem) {
      // زيادة الكمية إذا كان المنتج موجود
      setCartItems(cartItems.map(item => 
        item.id === product.id 
          ? { ...item, quantity: item.quantity + 1 }
          : item
      ));
    } else {
      // إضافة منتج جديد
      setCartItems([...cartItems, { ...product, quantity: 1 }]);
    }

    // حفظ السلة في localStorage
    const updatedCart = existingItem 
      ? cartItems.map(item => 
          item.id === product.id 
            ? { ...item, quantity: item.quantity + 1 }
            : item
        )
      : [...cartItems, { ...product, quantity: 1 }];
    
    localStorage.setItem('cartItems', JSON.stringify(updatedCart));

    // إظهار رسالة التأكيد
    setAddedProduct(product);
    setShowAddedMessage(true);
    
    // إخفاء الرسالة بعد 3 ثوان
    setTimeout(() => {
      setShowAddedMessage(false);
      setAddedProduct(null);
    }, 3000);
  };

  const handleCategoryClick = (categoryName: string) => {
    setSelectedCategory(categoryName);
  };

  const handleProductClick = (productId: number) => {
    navigate(`/product/${productId}`);
  };

  // تصفية المنتجات حسب الفئة المختارة
  const filteredProducts = selectedCategory 
    ? featuredProducts.filter(product => {
        switch(selectedCategory) {
          case 'القهوة الفاخرة':
            return product.category === 'coffee';
          case 'أدوات التحضير':
            return product.category === 'tools';
          case 'آلات القهوة':
            return product.category === 'machines';
          case 'الإكسسوارات':
            return product.category === 'accessories';
          default:
            return true;
        }
      })
    : featuredProducts;

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % bannerSlides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + bannerSlides.length) % bannerSlides.length);
  };

  // حساب إجمالي عدد المنتجات في السلة
  const totalCartItems = cartItems.reduce((total, item) => total + item.quantity, 0);

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Header cartItemsCount={totalCartItems} />
      
      {/* رسالة تأكيد الإضافة */}
      {showAddedMessage && addedProduct && (
        <div className="fixed top-20 left-4 right-4 z-50 bg-green-500 text-white p-4 rounded-lg shadow-lg transform transition-all duration-300">
          <div className="flex items-center justify-between" dir="rtl">
            <div className="flex items-center">
              <i className="ri-check-line text-xl ml-2"></i>
              <span className="font-medium">تم إضافة "{addedProduct.name}" إلى السلة</span>
            </div>
            <button 
              onClick={() => setShowAddedMessage(false)}
              className="text-white/80 hover:text-white"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
          </div>
        </div>
      )}
      
      {/* Main Content */}
      <main className="pt-20 pb-20 px-4">
        
        {/* Banner Slider */}
        <div className="relative mb-6 rounded-xl overflow-hidden">
          <div className="relative h-48">
            <img
              src={bannerSlides[currentSlide].image}
              alt={bannerSlides[currentSlide].title}
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-black/30 flex flex-col justify-center items-end p-6" dir="rtl">
              <h2 className="text-white text-xl font-bold mb-2">
                {bannerSlides[currentSlide].title}
              </h2>
              <p className="text-white/90 text-sm mb-4">
                {bannerSlides[currentSlide].subtitle}
              </p>
              <button className="bg-[#D4AF37] text-white px-6 py-2 rounded-full text-sm font-medium">
                {bannerSlides[currentSlide].buttonText}
              </button>
            </div>
          </div>
          
          {/* Slider Controls */}
          <button
            onClick={prevSlide}
            className="absolute left-4 top-1/2 transform -translate-y-1/2 w-8 h-8 bg-white/80 rounded-full flex items-center justify-center"
          >
            <i className="ri-arrow-left-s-line text-[#4B2E05]"></i>
          </button>
          <button
            onClick={nextSlide}
            className="absolute right-4 top-1/2 transform -translate-y-1/2 w-8 h-8 bg-white/80 rounded-full flex items-center justify-center"
          >
            <i className="ri-arrow-right-s-line text-[#4B2E05]"></i>
          </button>
          
          {/* Dots Indicator */}
          <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2">
            {bannerSlides.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentSlide(index)}
                className={`w-2 h-2 rounded-full transition-colors duration-200 ${
                  index === currentSlide ? 'bg-[#D4AF37]' : 'bg-white/50'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Categories Section */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4" dir="rtl">
            <h3 className="text-lg font-bold text-[#333333]">
              الأقسام الرئيسية
            </h3>
            {selectedCategory && (
              <button 
                onClick={() => setSelectedCategory(null)}
                className="text-[#D4AF37] text-sm font-medium flex items-center"
              >
                <i className="ri-close-line ml-1"></i>
                إلغاء التصفية
              </button>
            )}
          </div>
          <div className="grid grid-cols-2 gap-4">
            {categories.map((category) => (
              <div
                key={category.id}
                onClick={() => handleCategoryClick(category.name)}
                className={`bg-white rounded-xl p-4 shadow-md hover:shadow-lg transition-all duration-200 cursor-pointer ${
                  selectedCategory === category.name ? 'ring-2 ring-[#D4AF37] bg-[#D4AF37]/5' : ''
                }`}
              >
                <div className="flex flex-col items-center text-center">
                  <div className="w-16 h-16 mb-3 overflow-hidden rounded-full">
                    <img
                      src={category.icon}
                      alt={category.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <h4 className="font-semibold text-[#333333] text-sm mb-1">
                    {category.name}
                  </h4>
                  <span className="text-xs text-[#777777]">
                    {category.count} منتج
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Featured Products Section */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4" dir="rtl">
            <h3 className="text-lg font-bold text-[#333333]">
              {selectedCategory ? `منتجات ${selectedCategory}` : 'المنتجات المميزة'}
            </h3>
            <button className="text-[#D4AF37] text-sm font-medium">
              عرض الكل
            </button>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {filteredProducts.map((product) => (
              <ProductCard
                key={product.id}
                product={product}
                onAddToCart={handleAddToCart}
                onClick={() => handleProductClick(product.id)}
              />
            ))}
          </div>
          {filteredProducts.length === 0 && (
            <div className="text-center py-8">
              <i className="ri-search-line text-4xl text-[#777777] mb-4"></i>
              <p className="text-[#777777]">لا توجد منتجات في هذا القسم</p>
            </div>
          )}
        </div>

        {/* Special Offers Section */}
        <div className="bg-gradient-to-r from-[#4B2E05] to-[#6B4423] rounded-xl p-6 mb-6">
          <div className="text-center" dir="rtl">
            <h3 className="text-white text-xl font-bold mb-2">
              عروض خاصة لفترة محدودة
            </h3>
            <p className="text-white/90 text-sm mb-4">
              احصل على خصم 25% على جميع منتجات القهوة الفاخرة
            </p>
            <button className="bg-[#D4AF37] text-white px-8 py-3 rounded-full font-medium">
              تسوق الآن
            </button>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-white rounded-xl p-4 shadow-md text-center">
            <div className="w-12 h-12 bg-[#D4AF37]/10 rounded-full flex items-center justify-center mx-auto mb-3">
              <i className="ri-truck-line text-[#D4AF37] text-xl"></i>
            </div>
            <h4 className="font-semibold text-[#333333] text-sm mb-1">
              توصيل سريع
            </h4>
            <p className="text-xs text-[#777777]">
              خلال ساعتين
            </p>
          </div>
          
          <div className="bg-white rounded-xl p-4 shadow-md text-center">
            <div className="w-12 h-12 bg-[#D4AF37]/10 rounded-full flex items-center justify-center mx-auto mb-3">
              <i className="ri-customer-service-line text-[#D4AF37] text-xl"></i>
            </div>
            <h4 className="font-semibold text-[#333333] text-sm mb-1">
              دعم العملاء
            </h4>
            <p className="text-xs text-[#777777]">
              24/7
            </p>
          </div>
        </div>
      </main>

      <BottomNavigation />
    </div>
  );
}
